<!--
  FileViewer.vue

  The app's one file viewer, mounted in App.vue and opened through
  useFileViewer. Images, PDFs, audio, video and text render in place; other
  kinds offer the file in a new tab. ← and → step through the files it was
  opened on; Escape closes it.
-->
<template>
  <dialog ref="dialogRef" class="file-viewer" @cancel.prevent="close" @keydown="onKeydown">
    <template v-if="file">
      <header>
        <FileThumb :asset="file" :square="28" />
        <div class="title">
          <span class="name is-elided" :title="file.name ?? undefined">{{
            file.name || "Untitled"
          }}</span>
          <span class="meta">
            <template v-if="context">{{ context }} · </template>
            <template v-if="files.length > 1">{{ index + 1 }} of {{ files.length }} · </template>
            {{ details }}
          </span>
        </div>
        <button class="btn btn-sm" @click="copyLink">
          <span class="icon is-small"><font-awesome-icon icon="link" /></span>
          <span>Copy link</span>
        </button>
        <a class="btn btn-sm" :href="src ?? undefined" :download="file.name ?? true">
          <span class="icon is-small"><font-awesome-icon icon="download" /></span>
          <span>Download</span>
        </a>
        <a class="btn btn-sm" :href="src ?? undefined" target="_blank" rel="noopener noreferrer">
          <span class="icon is-small"><font-awesome-icon icon="arrow-up-right-from-square" /></span>
          <span>Open</span>
        </a>
        <button class="btn btn-sm is-transparent" aria-label="Close" @click="close">
          <span class="icon"><font-awesome-icon icon="xmark" /></span>
        </button>
      </header>

      <div class="stage">
        <button v-if="files.length > 1" class="btn step" aria-label="Previous file" @click="prev">
          <font-awesome-icon icon="chevron-left" />
        </button>

        <div :key="file.id" class="view">
          <img v-if="kind === 'image'" :src="src ?? undefined" :alt="file.name ?? 'Image'" />
          <iframe v-else-if="kind === 'pdf'" :src="src ?? undefined" :title="file.name ?? 'PDF'" />
          <video v-else-if="kind === 'video'" :src="src ?? undefined" controls autoplay />
          <div v-else-if="kind === 'audio'" class="audio">
            <FileThumb :asset="file" :height="160" />
            <audio :src="src ?? undefined" controls autoplay />
          </div>
          <pre v-else-if="kind === 'text' && text !== null">{{ text }}</pre>
          <div v-else class="fallback">
            <FileThumb :asset="file" :height="180" />
            <p class="text-muted">This file opens in its own app.</p>
            <a
              class="btn btn-primary"
              :href="src ?? undefined"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
            </a>
          </div>
        </div>

        <button v-if="files.length > 1" class="btn step" aria-label="Next file" @click="next">
          <font-awesome-icon icon="chevron-right" />
        </button>
      </div>
    </template>
  </dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { toast } from "vue-sonner"
import { assetKind } from "@auger/core/fields"
import { formatDate } from "@auger/ui/utils/date"
import { formatFileSize } from "@auger/ui/utils/number"
import { assetSrc, getAssetUrl } from "@/composables/useAssets"
import { useFileViewer } from "@/composables/useFileViewer"
import FileThumb from "./FileThumb.vue"

/** Text files past this many bytes open in a tab instead. */
const MAX_TEXT_BYTES = 512 * 1024

const { files, index, context, current: file, close, next, prev } = useFileViewer()
const dialogRef = ref<HTMLDialogElement | null>(null)

const kind = computed(() => {
  const k = assetKind(file.value?.mime_type)
  return k === "text" && (file.value?.size ?? 0) > MAX_TEXT_BYTES ? "other" : k
})
const src = computed(() => assetSrc(file.value))

const details = computed(() => {
  const f = file.value
  if (!f) return ""
  return [f.size ? formatFileSize(f.size, 1) : null, f.created_at ? formatDate(f.created_at) : null]
    .filter(Boolean)
    .join(" · ")
})

const text = ref<string | null>(null)
watch(
  [() => file.value?.id, kind],
  () => {
    text.value = null
    const url = src.value
    if (kind.value !== "text" || !url) return
    const id = file.value?.id
    fetch(url, { credentials: "include" })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((body) => {
        if (file.value?.id === id) text.value = body
      })
      .catch(() => {
        if (file.value?.id === id) text.value = "Could not load this file."
      })
  },
  { immediate: true }
)

watch(
  () => !!file.value,
  async (open) => {
    await nextTick()
    const dialog = dialogRef.value
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }
)

function onKeydown(e: KeyboardEvent) {
  const target = e.target instanceof Element ? e.target : null
  if (target?.closest("input, textarea, audio, video")) return
  if (e.key === "ArrowRight") next()
  else if (e.key === "ArrowLeft") prev()
}

function copyLink() {
  const url = getAssetUrl(file.value)
  if (!url) return
  navigator.clipboard
    .writeText(url)
    .then(() => toast.success("Link copied"))
    .catch(() => toast.error("Could not copy the link"))
}
</script>

<style>
.file-viewer {
  width: 100vw;
  height: 100dvh;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  /* A dark stage in both themes, as media viewers are. */
  background: color-mix(in srgb, black 88%, var(--auger-steel-gray-darker));
  color: #fff;

  &::backdrop {
    background: rgba(0, 0, 0, 0.6);
  }

  &[open] {
    display: flex;
    flex-direction: column;
  }

  header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: var(--auger-bg);
    color: var(--auger-text);
    border-bottom: 1px solid var(--auger-border);

    .title {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    .name {
      font-weight: 600;
      color: var(--auger-text-strong);
    }

    .meta {
      font-size: var(--auger-size-7);
      color: var(--auger-text-weak);
    }
  }

  .stage {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
  }

  .step {
    flex: none;
    border-radius: 50%;
    width: 2.75rem;
    height: 2.75rem;
  }

  .view {
    flex: 1;
    min-width: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img,
    video {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    iframe {
      width: 100%;
      height: 100%;
      border: 0;
      background: var(--auger-bg);
      border-radius: var(--auger-radius);
    }

    pre {
      width: 100%;
      max-width: 60rem;
      height: 100%;
      overflow: auto;
      margin: 0;
      white-space: pre-wrap;
      background: var(--auger-bg);
      color: var(--auger-text);
      border-radius: var(--auger-radius);
    }

    .audio,
    .fallback {
      width: min(28rem, 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;

      audio {
        width: 100%;
      }
    }
  }
}
</style>
